/**
 * Stage2InviteCopyButton (Plan 03-07 / HR1-07 / OPS-02) — fetches the pre-drafted
 * Stage 2 invite copy in a chosen language and writes it to the clipboard (inline —
 * never window.open per BUG-005). Language defaults to the position language.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useStage2InviteCopyQuery } from "./hooks";
import { mapErrorSlug } from "@/lib/error-slug-mapper";

type Lang = "es" | "en" | "de";

async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for older browsers (mobile Safari < 13.1).
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

export function Stage2InviteCopyButton({
  candidateId,
  positionLanguage,
}: {
  candidateId: number;
  positionLanguage: Lang;
}) {
  const { t } = useTranslation("admin-candidates");
  const [lang, setLang] = useState<Lang>(positionLanguage);
  const query = useStage2InviteCopyQuery(candidateId, lang);

  async function handleCopy() {
    try {
      const res = await query.refetch();
      if (res.data?.body_text) {
        await writeClipboard(res.data.body_text);
        toast.success(t("actions.invite_copied"));
      } else if (res.error) {
        toast.error(mapErrorSlug(res.error));
      } else {
        toast.error(t("actions.invite_failed"));
      }
    } catch (err) {
      toast.error(mapErrorSlug(err));
    }
  }

  return (
    <div className="flex items-center gap-2">
      <label className="sr-only" htmlFor={`invite-lang-${candidateId}`}>
        {t("invite.language_label")}
      </label>
      <select
        id={`invite-lang-${candidateId}`}
        value={lang}
        onChange={(e) => setLang(e.target.value as Lang)}
        className="min-h-[44px] rounded-md border border-input bg-background px-3 py-2 text-base"
      >
        <option value="es">ES</option>
        <option value="en">EN</option>
        <option value="de">DE</option>
      </select>
      <Button
        onClick={handleCopy}
        disabled={query.isFetching}
        className="min-h-[44px]"
        data-testid="stage2-invite-copy"
      >
        {t("actions.copy_invite")}
      </Button>
    </div>
  );
}
